import React, { useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { LogIn, AlertTriangle, Lock } from 'lucide-react';
import PasswordInput from '@/components/marketplace/PasswordInput';
import TrustPanel from '@/components/marketplace/TrustPanel';

const formatUnlock = (iso) => {
  try {
    return new Date(iso).toLocaleString('en-IN', {
      day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true,
    });
  } catch { return iso; }
};

const Login = () => {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [attemptsLeft, setAttemptsLeft] = useState(null);
  const [lockedInfo, setLockedInfo] = useState(null);

  const from = location.state?.from || '/dashboard';

  const submit = async (e) => {
    e.preventDefault();
    if (!email || !password) return toast.error('Please fill all fields');
    if (lockedInfo) return;
    setLoading(true);
    setAttemptsLeft(null);
    try {
      const user = await login(email, password);
      toast.success('Welcome back to IndusFact');
      // Post-login landing follows granted permissions, so custom staff roles
      // land on the panel they can actually use.
      const perms = user.permissions || [];
      if (perms.includes('admin.access')) navigate('/admin');
      else if (perms.includes('moderator.access')) navigate('/moderator');
      else navigate(from);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (detail && typeof detail === 'object') {
        if (detail.code === 'account_locked' || detail.code === 'account_locked_now') {
          setLockedInfo(detail);
          setAttemptsLeft(null);
          toast.error(detail.message || 'Account locked');
        } else {
          if (typeof detail.attempts_left === 'number') setAttemptsLeft(detail.attempts_left);
          toast.error(detail.message || 'Login failed');
        }
      } else {
        toast.error(detail || 'Login failed');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='min-h-[80vh] indus-container py-10 flex items-center justify-center'>
      <Card className='w-full max-w-md p-8'>
        <div className='flex items-center gap-2 mb-6'>
          <span className='inline-flex h-9 w-9 items-center justify-center rounded-lg bg-white border overflow-hidden'>
            <img src='/indusfact-logo.png' alt='IndusFact' className='h-8 w-8 object-contain' />
          </span>
          <div>
            <div className='font-semibold text-lg'>Sign in to IndusFact</div>
            <div className='text-xs text-muted-foreground'>Post listings, reveal contact, and manage inquiries</div>
          </div>
        </div>

        {lockedInfo && (
          <div
            className='mb-4 rounded-lg border border-[hsl(var(--danger))]/40 bg-[hsl(var(--danger))]/10 p-3 text-sm'
            data-testid='login-locked-banner'
          >
            <div className='flex items-start gap-2 text-[hsl(var(--danger))]'>
              <Lock className='h-4 w-4 mt-0.5 shrink-0' />
              <div>
                <div className='font-semibold'>Account temporarily locked</div>
                <div className='text-foreground/80 mt-1'>{lockedInfo.message}</div>
                {lockedInfo.lock_until && (
                  <div className='mt-1 text-xs text-muted-foreground'>
                    Try again after: <strong>{formatUnlock(lockedInfo.lock_until)}</strong>
                  </div>
                )}
                <div className='mt-2 text-xs'>
                  Forgot password?{' '}
                  <Link to='/forgot-password' className='text-primary hover:underline'>Reset it now</Link>
                  {' — '}the reset link works even while the account is locked.
                </div>
              </div>
            </div>
          </div>
        )}

        {!lockedInfo && attemptsLeft !== null && attemptsLeft <= 2 && (
          <div
            className='mb-4 rounded-lg border border-yellow-500/40 bg-yellow-500/10 p-3 text-xs text-yellow-800 dark:text-yellow-500 flex items-start gap-2'
            data-testid='login-attempts-warning'
          >
            <AlertTriangle className='h-4 w-4 mt-0.5 shrink-0' />
            <div>
              <strong>{attemptsLeft} attempt(s) remaining</strong> before your account is locked for 2 hours.
              If you don&apos;t remember your password,{' '}
              <Link to='/forgot-password' className='underline'>reset it now</Link>.
            </div>
          </div>
        )}

        <form onSubmit={submit} className='space-y-4'>
          <div>
            <Label htmlFor='email'>Email</Label>
            <Input id='email' type='email' data-testid='login-email-input' value={email} onChange={(e) => { setEmail(e.target.value); setLockedInfo(null); }} placeholder='you@company.com' autoComplete='email' disabled={loading} />
          </div>
          <div>
            <Label htmlFor='password'>Password</Label>
            <PasswordInput
              id='password'
              data-testid='login-password-input'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder='••••••••'
              autoComplete='current-password'
              disabled={loading || !!lockedInfo}
            />
          </div>
          <Button className='w-full' type='submit' disabled={loading || !!lockedInfo} data-testid='login-submit-button'>
            <LogIn className='h-4 w-4 mr-1.5' /> {loading ? 'Signing in…' : (lockedInfo ? 'Account locked' : 'Sign in')}
          </Button>
        </form>
        <div className='mt-4 text-xs text-muted-foreground text-center'>
          Don&apos;t have an account?{' '}
          <Link to='/register' className='text-primary hover:underline'>Create one</Link>
          {' · '}
          <Link to='/forgot-password' className='text-primary hover:underline'>Forgot password?</Link>
        </div>

        <TrustPanel />
      </Card>
    </div>
  );
};

export default Login;
